import { useEffect, useMemo, useRef, useState } from 'react';
import { usePlan } from '../store/planStore.js';
import { toWeekPlan } from '../store/db.js';
import { buildShoppingList } from '../../domain/shoppingList.js';
import { householdPortions } from '../../domain/portions.js';
import { AISLE_LABELS, AISLE_ORDER, aisleRank } from '../../domain/aisles.js';
import { PACK_SIZES } from '../../data/index.js';
import type { AisleCategory, ShoppingLine } from '../../domain/types.js';
import { TickRow } from '../components/TickRow.js';
import { staplesNeeded } from './cupboard.js';
import { dinnerExtras } from '../../domain/requirements.js';
import { useMeals } from '../recipes/useMeals.js';
import { extraLineLabel, withoutUnresolved } from '../recipes/resolve.js';

interface Row {
  key: string;
  line: ShoppingLine;
  /** Only something the person typed onto the list can be taken off it. */
  removable: boolean;
}

/** How long the strike takes to draw, before a row is allowed to move. */
const SETTLE_MS = 380;

export function ShoppingList(): React.JSX.Element {
  const { ready, plan, settings, toggleTick, addManual, removeManual } = usePlan();
  const catalogue = useMeals();
  const { plan: buildable, missing } = useMemo(
    () => withoutUnresolved(plan, catalogue.byId),
    [plan, catalogue],
  );
  const [settling, setSettling] = useState<ReadonlySet<string>>(new Set());
  const timers = useRef(new Map<string, number>());
  const [extra, setExtra] = useState('');
  const [extraAisle, setExtraAisle] = useState<AisleCategory>('household');

  // A household of zero is what Settings presents as a legitimate resting
  // state (each `−` clamps at 0), but buildShoppingList's own
  // `if (req.qty === 0) continue` then drops every dinner ingredient without a
  // word — a complete, plausible-looking, and completely wrong list (34 rows
  // became 14 with nothing to say so). Catch it before building anything.
  const household = ready ? householdPortions(settings.people) : null;

  // Cancel anything pending on the way out, so a timer cannot outlive the screen.
  useEffect(
    () => () => {
      for (const id of timers.current.values()) window.clearTimeout(id);
      timers.current.clear();
    },
    [],
  );

  const tick = (key: string): void => {
    const wasTicked = plan.ticked[key] === true;
    toggleTick(key);

    // Always cancel this row's pending timer first. Tick, untick, re-tick
    // inside the window would otherwise leave the FIRST timer live, and it
    // fires mid-strike on the second tick — sinking the row with the line
    // only part-drawn.
    const pending = timers.current.get(key);
    if (pending !== undefined) window.clearTimeout(pending);
    timers.current.delete(key);

    if (wasTicked) {
      setSettling((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
      return;
    }

    setSettling((current) => new Set(current).add(key));
    timers.current.set(
      key,
      window.setTimeout(() => {
        timers.current.delete(key);
        setSettling((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }, SETTLE_MS),
    );
  };

  /**
   * Every row carries its own key. Deriving one from the line (`ingredientId ??
   * label`) put manual items under their LABEL, while the store deletes their
   * tick by ID — so deleting an extra orphaned its tick and re-adding it came
   * back already crossed off. And a label that happened to equal an ingredient
   * id ("milk", typed lowercase) produced two rows with one React key: one tap
   * ticked both, and React is free to drop one of them entirely.
   */
  const rows = useMemo<Row[]>(() => {
    if (!ready || household === 0) return [];
    const week = toWeekPlan(buildable);
    const derived: Row[] = buildShoppingList({
      plan: week,
      people: settings.people,
      lookups: catalogue.lookups,
      packSizes: PACK_SIZES,
    }).map((line) => ({ key: line.ingredientId ?? line.label, line, removable: false }));

    // Not in the catalogue, so never pack-rounded and never merged with a
    // catalogue line. Keyed `extra:…` so a tick cannot collide with one.
    const extras: Row[] = dinnerExtras(week.dinners, householdPortions(settings.people), catalogue.lookups)
      .map((e) => ({
        key: e.key,
        removable: false,
        line: {
          ingredientId: null, label: extraLineLabel(e), requiredQty: 1, unit: 'each',
          packLabel: null, isUnpacked: false, aisle: e.aisle, sources: e.sources,
        },
      }));

    const manual: Row[] = plan.manual.map((item) => ({
      key: item.id,
      removable: true,
      line: {
        ingredientId: null,
        label: item.label,
        requiredQty: 1,
        unit: 'each',
        packLabel: null,
        isUnpacked: false,
        aisle: item.aisle,
        sources: [],
      },
    }));

    // Sorting EVERY row through aisleRank, manual ones included. The engine
    // sorts what it produces, but manual lines are appended afterwards — and
    // an aisle missing from a store's layout made them vanish from the screen
    // while still counting in the progress total. A list that says "0 of 2"
    // and shows nothing is the silent-drop failure wearing a counter.
    return [...derived, ...extras, ...manual].sort(
      (a, b) => aisleRank(plan.store, a.line.aisle) - aisleRank(plan.store, b.line.aisle),
    );
  }, [ready, household, plan, buildable, settings.people, catalogue]);

  // Cupboard staples the plan needs but a stale "got it" from a week ago kept
  // off this list — bypassing CupboardCheck entirely to get here. Bounded to
  // staples by applyPantry/staplesNeeded, so no perishable is ever silently
  // withheld this way; this is purely so the exclusion is visible rather than
  // invisible.
  const cupboardOff = useMemo(() => {
    if (!ready || household === 0) return 0;
    return staplesNeeded(plan, settings.people, catalogue).filter((id) => plan.pantry[id] === true).length;
  }, [ready, household, plan, settings.people, catalogue]);

  if (!ready) return <p>Loading</p>;

  if (household === 0) {
    return (
      <section className="list">
        <p className="empty-note">
          Nobody is set to eat, so every meal would buy nothing. Set portion factors
          in Settings.
        </p>
      </section>
    );
  }

  const done = rows.filter((r) => plan.ticked[r.key] === true).length;

  const byAisle = new Map<AisleCategory, Row[]>();
  for (const row of rows) {
    byAisle.set(row.line.aisle, [...(byAisle.get(row.line.aisle) ?? []), row]);
  }

  const syncingNote = missing.length > 0 ? (
    <p className="nudge" data-testid="still-syncing">
      {missing.length} recipe{missing.length === 1 ? '' : 's'} still syncing from the other
      phone, so {missing.length === 1 ? 'it is' : 'they are'} not on this list yet. Reopen the
      app to refresh.
    </p>
  ) : null;

  const addExtraForm = (
    <form
      className="add-extra"
      onSubmit={(e) => {
        e.preventDefault();
        const label = extra.trim();
        if (label.length === 0) return;
        addManual(label, extraAisle);
        setExtra('');
      }}
    >
      <input
        className="search"
        placeholder="Add something else"
        value={extra}
        onChange={(e) => setExtra(e.target.value)}
      />
      <label className="visually-hidden" htmlFor="extra-aisle">Aisle</label>
      <select
        id="extra-aisle"
        value={extraAisle}
        onChange={(e) => setExtraAisle(e.target.value as AisleCategory)}
      >
        {AISLE_ORDER[plan.store].map((aisle) => (
          <option key={aisle} value={aisle}>{AISLE_LABELS[aisle]}</option>
        ))}
      </select>
      <button type="submit">Add</button>
    </form>
  );

  if (rows.length === 0) {
    return (
      <section className="list">
        {syncingNote}
        <p className="empty-note">
          {plan.dinners.length === 0 && plan.staples.length === 0
            ? 'Nothing on the list yet — plan some meals first.'
            : missing.length > 0 && buildable.dinners.length === 0
              // Empty only because every dinner is waiting on its recipe:
              // "it's all in the cupboard" would be untrue.
              ? "Nothing to buy yet — this week's dinners are still syncing."
              : 'Nothing to buy — everything this week needs is already in the cupboard.'}
        </p>
        {addExtraForm}
      </section>
    );
  }

  return (
    <section className="list">
      {syncingNote}
      <header className="list-head">
        <p className="progress" data-testid="progress">
          {done} of {rows.length} in the trolley
        </p>
        {cupboardOff > 0 && (
          <p className="plan-count" data-testid="cupboard-off">
            {cupboardOff} cupboard item{cupboardOff === 1 ? '' : 's'} left off — you said you had{' '}
            {cupboardOff === 1 ? 'it' : 'them'}
          </p>
        )}
        <div className="track">
          <div className="bar" style={{ width: `${Math.round((done / rows.length) * 100)}%` }} />
        </div>
      </header>

      {AISLE_ORDER[plan.store]
        .filter((aisle) => (byAisle.get(aisle) ?? []).length > 0)
        .map((aisle) => {
          const inAisle = byAisle.get(aisle) ?? [];
          // Ticked rows sink to the bottom of their aisle — never disappear —
          // but not until the strike has drawn. Moving instantly meant the
          // confirmation animation played somewhere your eye was not, and
          // every row below jumped up under your finger, so tapping quickly
          // down an aisle ticked the wrong thing.
          const sunk = (row: Row): boolean =>
            plan.ticked[row.key] === true && !settling.has(row.key);
          const sorted = [...inAisle.filter((r) => !sunk(r)), ...inAisle.filter(sunk)];
          return (
            <section className="aisle" key={aisle}>
              <h2 className="aisle-head">{AISLE_LABELS[aisle]}</h2>
              <ul className="items">
                {sorted.map((row) => (
                  <TickRow
                    key={row.key}
                    rowKey={row.key}
                    line={row.line}
                    ticked={plan.ticked[row.key] === true}
                    onToggle={() => tick(row.key)}
                    onRemove={row.removable ? () => removeManual(row.key) : null}
                  />
                ))}
              </ul>
            </section>
          );
        })}

      {addExtraForm}
    </section>
  );
}
